class FullscreenSplitSlider {
  constructor(section) {
    this.section = section;
    this.wrapper = section.querySelector(".fullscreen-split__wrapper");
    this.cells = section.querySelectorAll(".fullscreen-split__cell");
    this.indicators = section.querySelectorAll(".fullscreen-split__indicator");
    this.pageIndicators = section.querySelectorAll(
      ".fullscreen-split__page-indicator-line"
    );
    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.startY = 0;
    this.currentY = 0;
    this.translateX = 0;
    this.hasMoved = false;

    if (!this.wrapper || this.cells.length <= 1) return;

    this.init();
  }

  init() {
    // Initialize indicators
    this.updateIndicators();

    // Check if we're in mobile/tablet viewport
    if (window.innerWidth <= 989) {
      this.setupSwipe();
    } else {
      // Desktop: ensure cells are side by side
      this.setupDesktop();
    }

    // Handle resize with debouncing
    let resizeTimeout;
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth <= 989) {
          // Re-setup swipe if not already set up
          if (!this.touchStartHandler) {
            this.setupSwipe();
          } else {
            // Just update dimensions
            const sectionWidth = this.section.offsetWidth || window.innerWidth;
            this.wrapper.style.width = `${sectionWidth * this.cells.length}px`;
            this.cells.forEach((cell) => {
              cell.style.width = `${sectionWidth}px`;
              cell.style.minWidth = `${sectionWidth}px`;
              cell.style.maxWidth = `${sectionWidth}px`;
              cell.style.flex = "0 0 auto";
              // Keep height consistent
              cell.style.height = "100%";
              cell.style.minHeight = "100%";
            });
            this.slideToIndex(this.currentIndex);
          }
        } else {
          this.setupDesktop();
          this.removeSwipe();
        }
      }, 150);
    };
    window.addEventListener("resize", this.resizeHandler);
  }

  setupDesktop() {
    // Reset wrapper width for desktop
    this.wrapper.style.width = "100%";
    this.wrapper.style.height = "100%";
    this.wrapper.style.minHeight = "100%";
    this.wrapper.style.transform = "";
    this.wrapper.style.transition = "";

    // Reset cell widths for desktop, but keep height
    this.cells.forEach((cell) => {
      cell.style.width = "";
      cell.style.minWidth = "";
      cell.style.maxWidth = "";
      cell.style.flex = "";
      // Keep height consistent
      cell.style.height = "100%";
      cell.style.minHeight = "100%";
    });
  }

  setupSwipe() {
    // Remove any existing listeners first
    this.removeSwipe();

    // Wait for next frame to ensure layout is calculated
    requestAnimationFrame(() => {
      // Set wrapper width to accommodate all cells
      const sectionWidth = this.section.offsetWidth || window.innerWidth;
      const totalWidth = sectionWidth * this.cells.length;
      this.wrapper.style.width = `${totalWidth}px`;
      // Keep height consistent - don't change it
      this.wrapper.style.height = "100%";
      this.wrapper.style.minHeight = "100%";
      this.wrapper.style.transform = `translateX(0)`;
      this.currentIndex = 0;
      this.updateIndicators();

      // Update cell widths for mobile, but keep height consistent
      this.cells.forEach((cell) => {
        cell.style.width = `${sectionWidth}px`;
        cell.style.minWidth = `${sectionWidth}px`;
        cell.style.maxWidth = `${sectionWidth}px`;
        cell.style.flex = "0 0 auto";
        // Ensure height stays the same
        cell.style.height = "100%";
        cell.style.minHeight = "100%";
      });

      // Attach event listeners after layout is set
      this.attachSwipeListeners();
    });
  }

  attachSwipeListeners() {
    // Touch events - attach to wrapper for better control
    this.touchStartHandler = this.handleTouchStart.bind(this);
    this.touchMoveHandler = this.handleTouchMove.bind(this);
    this.touchEndHandler = this.handleTouchEnd.bind(this);
    this.touchCancelHandler = this.handleTouchEnd.bind(this);

    // Attach to wrapper and section for better coverage
    this.wrapper.addEventListener("touchstart", this.touchStartHandler, {
      passive: false,
      capture: false,
    });
    this.wrapper.addEventListener("touchmove", this.touchMoveHandler, {
      passive: false,
      capture: false,
    });
    this.wrapper.addEventListener("touchend", this.touchEndHandler, {
      passive: true,
    });
    this.wrapper.addEventListener("touchcancel", this.touchCancelHandler, {
      passive: true,
    });

    // Also attach to section as fallback
    this.section.addEventListener("touchstart", this.touchStartHandler, {
      passive: false,
      capture: true,
    });
    this.section.addEventListener("touchmove", this.touchMoveHandler, {
      passive: false,
      capture: true,
    });

    // Mouse events for desktop testing and browser dev tools responsive mode
    // These are essential for responsive mode testing
    this.mouseDownHandler = this.handleMouseDown.bind(this);
    this.mouseMoveHandler = this.handleMouseMove.bind(this);
    this.mouseUpHandler = this.handleMouseUp.bind(this);
    this.mouseLeaveHandler = this.handleMouseUp.bind(this);

    // Attach mouse events to both wrapper and section
    this.wrapper.addEventListener("mousedown", this.mouseDownHandler, {
      passive: false,
    });
    this.section.addEventListener("mousedown", this.mouseDownHandler, {
      passive: false,
      capture: true,
    });

    // Use document for move/up to catch events even if mouse leaves element
    document.addEventListener("mousemove", this.mouseMoveHandler, {
      passive: false,
    });
    document.addEventListener("mouseup", this.mouseUpHandler, {
      passive: false,
    });
    document.addEventListener("mouseleave", this.mouseUpHandler, {
      passive: false,
    });
  }

  removeSwipe() {
    this.wrapper.style.transform = "";
    this.wrapper.style.transition = "";
    this.isDragging = false;

    // Reset cell widths for desktop, but keep height
    this.cells.forEach((cell) => {
      cell.style.width = "";
      cell.style.minWidth = "";
      cell.style.maxWidth = "";
      cell.style.flex = "";
      // Keep height consistent
      cell.style.height = "100%";
      cell.style.minHeight = "100%";
    });

    // Remove event listeners from wrapper
    if (this.touchStartHandler) {
      this.wrapper.removeEventListener("touchstart", this.touchStartHandler);
      this.wrapper.removeEventListener("touchmove", this.touchMoveHandler);
      this.wrapper.removeEventListener("touchend", this.touchEndHandler);
      if (this.touchCancelHandler) {
        this.wrapper.removeEventListener(
          "touchcancel",
          this.touchCancelHandler
        );
      }
    }

    // Remove event listeners from section
    if (this.touchStartHandler) {
      this.section.removeEventListener("touchstart", this.touchStartHandler, {
        capture: true,
      });
      this.section.removeEventListener("touchmove", this.touchMoveHandler, {
        capture: true,
      });
    }

    // Remove mouse listeners
    if (this.mouseDownHandler) {
      this.wrapper.removeEventListener("mousedown", this.mouseDownHandler);
      this.section.removeEventListener("mousedown", this.mouseDownHandler, {
        capture: true,
      });
      document.removeEventListener("mousemove", this.mouseMoveHandler);
      document.removeEventListener("mouseup", this.mouseUpHandler);
      document.removeEventListener("mouseleave", this.mouseUpHandler);
    }
  }

  handleTouchStart(e) {
    if (e.touches.length !== 1) return; // Only handle single touch

    // Don't interfere with button clicks or links
    const target = e.target;
    if (
      target.closest("button") ||
      target.closest(".fullscreen-split__button")
    ) {
      return;
    }

    // Allow cell links to work, but still enable swipe
    // We'll check if it's a swipe or a tap in handleTouchEnd

    this.isDragging = true;
    this.startX = e.touches[0].clientX;
    const sectionWidth = this.section.offsetWidth || window.innerWidth;
    this.translateX = -this.currentIndex * sectionWidth;
    this.wrapper.style.transition = "none";
    this.startY = e.touches[0].clientY; // Track Y for vertical scroll detection
    this.hasMoved = false;
  }

  handleTouchMove(e) {
    if (!this.isDragging || e.touches.length !== 1) return;

    this.currentX = e.touches[0].clientX;
    this.currentY = e.touches[0].clientY;

    const diffX = this.currentX - this.startX;
    const diffY = Math.abs(this.currentY - this.startY);

    // Check if we've moved enough to consider it a swipe
    if (Math.abs(diffX) > 5 || Math.abs(diffY) > 5) {
      this.hasMoved = true;
    }

    // Prevent default if horizontal swipe is dominant
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      e.preventDefault();
      e.stopPropagation();
    }

    const sectionWidth = this.section.offsetWidth || window.innerWidth;
    const newTranslateX = this.translateX + diffX;

    // Limit the drag range
    const minTranslate = -((this.cells.length - 1) * sectionWidth);
    const maxTranslate = 0;
    const constrainedTranslate = Math.max(
      minTranslate,
      Math.min(maxTranslate, newTranslateX)
    );

    this.wrapper.style.transform = `translateX(${constrainedTranslate}px)`;
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;

    const diff = this.currentX - this.startX;
    const sectionWidth = this.section.offsetWidth || window.innerWidth;
    const threshold = sectionWidth * 0.2; // 20% threshold

    // If it's a small movement (tap), allow cell link to work
    if (Math.abs(diff) < 10 && !this.hasMoved) {
      // Small tap - allow default link behavior
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      return;
    }

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && this.currentIndex > 0) {
        // Swipe right - go to previous
        this.currentIndex--;
      } else if (diff < 0 && this.currentIndex < this.cells.length - 1) {
        // Swipe left - go to next
        this.currentIndex++;
      }
    }

    this.slideToIndex(this.currentIndex);
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
  }

  handleMouseDown(e) {
    // Don't interfere with button clicks
    const target = e.target;
    if (
      target.closest("button") ||
      target.closest(".fullscreen-split__button")
    ) {
      return;
    }

    // Allow cell links to work, but still enable drag
    // We'll check if it's a drag or a click in handleMouseUp

    this.isDragging = true;
    this.startX = e.clientX;
    this.startY = e.clientY;
    const sectionWidth = this.section.offsetWidth || window.innerWidth;
    this.translateX = -this.currentIndex * sectionWidth;
    this.wrapper.style.transition = "none";
    this.hasMoved = false;
    e.preventDefault();
    e.stopPropagation();
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;

    this.currentX = e.clientX;
    this.currentY = e.clientY;

    const diffX = this.currentX - this.startX;
    const diffY = Math.abs(this.currentY - this.startY);

    // Check if we've moved enough to consider it a swipe
    if (Math.abs(diffX) > 5 || Math.abs(diffY) > 5) {
      this.hasMoved = true;
    }

    // Only prevent default if horizontal swipe is dominant
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      e.preventDefault();
      e.stopPropagation();
    }

    const sectionWidth = this.section.offsetWidth || window.innerWidth;
    const newTranslateX = this.translateX + diffX;

    const minTranslate = -((this.cells.length - 1) * sectionWidth);
    const maxTranslate = 0;
    const constrainedTranslate = Math.max(
      minTranslate,
      Math.min(maxTranslate, newTranslateX)
    );

    this.wrapper.style.transform = `translateX(${constrainedTranslate}px)`;
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;

    const diff = this.currentX - this.startX;
    const sectionWidth = this.section.offsetWidth || window.innerWidth;
    const threshold = sectionWidth * 0.2;

    // If it's a small movement (click), allow cell link to work
    if (Math.abs(diff) < 10 && !this.hasMoved) {
      // Small click - allow default link behavior
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.startY = 0;
      this.currentY = 0;
      return;
    }

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && this.currentIndex > 0) {
        // Swipe right - go to previous
        this.currentIndex--;
      } else if (diff < 0 && this.currentIndex < this.cells.length - 1) {
        // Swipe left - go to next
        this.currentIndex++;
      }
    }

    this.slideToIndex(this.currentIndex);
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.startY = 0;
    this.currentY = 0;
  }

  slideToIndex(index) {
    this.currentIndex = index;
    const sectionWidth = this.section.offsetWidth || window.innerWidth;
    this.wrapper.style.transition =
      "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)";
    this.wrapper.style.transform = `translateX(-${index * sectionWidth}px)`;
    this.updateIndicators();
  }

  updateIndicators() {
    this.indicators.forEach((indicator, index) => {
      if (index === this.currentIndex) {
        indicator.classList.add("fullscreen-split__indicator--active");
      } else {
        indicator.classList.remove("fullscreen-split__indicator--active");
      }
    });
    this.pageIndicators.forEach((indicator, index) => {
      if (index === this.currentIndex) {
        indicator.classList.add(
          "fullscreen-split__page-indicator-line--active"
        );
      } else {
        indicator.classList.remove(
          "fullscreen-split__page-indicator-line--active"
        );
      }
    });
  }
}

// Initialize all fullscreen split sections
function initFullscreenSplit() {
  const sections = document.querySelectorAll('[id^="FullscreenSplit-"]');
  sections.forEach((section) => {
    // Check if already initialized
    if (!section.dataset.initialized) {
      try {
        new FullscreenSplitSlider(section);
        section.dataset.initialized = "true";
      } catch (error) {
        console.error("Error initializing FullscreenSplitSlider:", error);
      }
    }
  });
}

// Initialize immediately if DOM is ready, otherwise wait
function tryInit() {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      // Wait a bit for layout to settle
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          initFullscreenSplit();
        });
      });
    });
  } else {
    // DOM already loaded, wait for next frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        initFullscreenSplit();
      });
    });
  }
}

tryInit();

// Re-initialize on Shopify theme editor changes
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.detail.querySelector('[id^="FullscreenSplit-"]');
    if (section) {
      section.dataset.initialized = "false";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            new FullscreenSplitSlider(section);
            section.dataset.initialized = "true";
          } catch (error) {
            console.error("Error initializing FullscreenSplitSlider:", error);
          }
        });
      });
    }
  });
}
